import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    Building2, LayoutDashboard, Users, DollarSign, Calendar, Clock, BarChart3,
    Settings, LogOut, ChevronDown, Menu, X, FileText, Wallet, CreditCard,
    MapPin, Cog, Target, Receipt, ClipboardList, FileSpreadsheet, TrendingUp,
    Shield, Briefcase, Award, Home, User as UserIcon, Search, Sparkles, Activity, Heart, GraduationCap, Target as TargetIcon, Crown, Network
} from 'lucide-react';
import NotificationBell from './NotificationBell';

const Layout = ({ children }) => {
    const { user, logout, canManage } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    useEffect(() => {
        if ('Notification' in window && Notification.permission === 'default') {
            Notification.requestPermission();
        }
    }, []);

    useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

    const handleLogout = async () => { await logout(); navigate('/login'); };

    // Grouped navigation — premium IA
    const navGroups = [
        {
            label: 'Workspace',
            items: [
                { name: 'CEO View', href: '/exec', icon: Crown, roles: ['admin', 'hr_manager'] },
                { name: 'Dashboard', href: '/dashboard', icon: Home, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'My Performance', href: '/performance', icon: Target, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Goals & OKRs', href: '/goals', icon: TargetIcon, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Engagement', href: '/engagement', icon: Heart, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Learning', href: '/learning', icon: GraduationCap, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Profile', href: '/profile', icon: UserIcon, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
            ],
        },
        {
            label: 'CRM',
            items: [
                { name: 'CRM Leads', href: '/crm-leads', icon: Briefcase, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'CRM Dashboard', href: '/crm-dashboard', icon: Award, roles: ['admin', 'hr_manager'] },
                { name: 'Field Visits', href: '/visits', icon: MapPin, roles: ['admin', 'hr_manager', 'employee'] },
                { name: 'Geo Tracking', href: '/geo-tracking', icon: MapPin, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
            ],
        },
        {
            label: 'HR & Payroll',
            items: [
                { name: 'Employees', href: '/employees', icon: Users, roles: ['admin', 'hr_manager'] },
                { name: 'Org Chart', href: '/org-chart', icon: Network },
                { name: 'Payroll', href: '/payroll', icon: DollarSign, roles: ['admin', 'hr_manager'] },
                { name: 'My Payslips', href: '/my-payslips', icon: FileText, roles: ['employee'] },
                { name: 'Attendance', href: '/attendance', icon: Clock, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Attendance Analytics', href: '/attendance-analytics', icon: TrendingUp, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Leave Management', href: '/leaves', icon: Calendar, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Loans', href: '/loans', icon: Wallet, roles: ['admin', 'hr_manager'] },
                { name: 'Advances', href: '/advances', icon: CreditCard, roles: ['admin', 'hr_manager'] },
                { name: 'Insurance', href: '/insurance', icon: Shield, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Onboarding', href: '/onboarding', icon: ClipboardList, roles: ['admin', 'hr_manager'] },
                // For employees: show "My Self-Onboarding" if not yet completed
                ...(user?.role === 'employee' && user?.onboarding_completed === false
                    ? [{ name: 'My Self-Onboarding', href: '/self-onboarding', icon: ClipboardList, badge: 'TODO' }]
                    : []),
            ],
        },
        {
            label: 'Operations',
            items: [
                { name: 'Projects', href: '/projects', icon: LayoutDashboard, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Tasks', href: '/tasks', icon: FileText, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Assets', href: '/assets', icon: Building2, roles: ['admin', 'hr_manager'] },
                { name: 'Expenses', href: '/expenses', icon: Receipt, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
                { name: 'Activity', href: '/activity', icon: BarChart3, roles: ['admin', 'hr_manager'] },
            ],
        },
        {
            label: 'Reports & Setup',
            items: [
                { name: 'Reports', href: '/reports', icon: FileSpreadsheet, roles: ['admin', 'hr_manager'] },
                { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['admin', 'hr_manager'] },
                { name: 'Audit Log', href: '/audit-log', icon: Activity, roles: ['admin', 'hr_manager'] },
                { name: 'Departments', href: '/departments', icon: Building2, roles: ['admin', 'hr_manager'] },
                { name: 'Company Setup', href: '/company-setup', icon: Cog, roles: ['admin', 'hr_manager'] },
            ],
        },
    ];

    // Mobile bottom nav (5 items max for RM)
    const mobileNav = [
        { name: 'Home', href: '/dashboard', icon: Home, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
        { name: 'KPI', href: '/performance', icon: Target, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
        { name: 'Leads', href: '/crm-leads', icon: Briefcase, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
        { name: 'Visits', href: '/visits', icon: MapPin, roles: ['admin', 'hr_manager', 'employee'] },
        { name: 'Profile', href: '/profile', icon: UserIcon, roles: ['admin', 'hr_manager', 'manager', 'employee'] },
    ].filter(i => !i.roles || i.roles.includes(user?.role));

    const getInitials = (name) => name ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'U';
    const filterByRole = (items) => items.filter(i => !i.roles || i.roles.includes(user?.role));

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Mobile sidebar backdrop */}
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            {/* Premium Dark Sidebar */}
            <aside className={`fixed top-0 left-0 z-50 h-full w-72 transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ background: '#0a1628', borderRight: '1px solid #1e293b' }}>
                <div className="flex flex-col h-full">
                    {/* Logo */}
                    <div className="flex items-center gap-3 px-5 h-[72px] border-b" style={{ borderColor: '#1e293b' }}>
                        <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-white/95 p-0.5 flex items-center justify-center">
                            <img src="/digi-loans-logo.png" alt="DIGI-LOANS" className="h-full w-full object-contain" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-black tracking-tight leading-none" style={{ fontFamily: 'Chivo, sans-serif', fontSize: 16 }}>DIGI-LOANS</p>
                            <p className="text-[10px] text-slate-400 mt-0.5 tracking-wider uppercase">Loan Simplified</p>
                        </div>
                        <button className="lg:hidden text-slate-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Grouped Navigation */}
                    <nav className="flex-1 px-3 py-4 overflow-y-auto chrome-scroll">
                        {navGroups.map((group, gi) => {
                            const items = filterByRole(group.items);
                            if (items.length === 0) return null;
                            return (
                                <div key={`g-${gi}`} className="mb-5">
                                    <p className="px-4 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1.5">{group.label}</p>
                                    <div className="space-y-0.5">
                                        {items.map((item) => {
                                            const isActive = location.pathname === item.href;
                                            return (
                                                <Link
                                                    key={item.name}
                                                    to={item.href}
                                                    className={`group relative flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-all rounded-md mx-1
                                                        ${isActive ? 'text-white' : 'text-slate-300 hover:text-white'}`}
                                                    style={isActive ? { background: '#111c2d', borderLeft: '3px solid #16a34a', paddingLeft: '13px' } : {}}
                                                    onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = '#111c2d'; }}
                                                    onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = ''; }}
                                                    data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                                                >
                                                    <item.icon className={`h-[18px] w-[18px] flex-shrink-0 ${isActive ? 'text-emerald-400' : 'text-slate-400 group-hover:text-slate-200'}`} />
                                                    <span className="truncate flex-1">{item.name}</span>
                                                    {item.badge && (
                                                        <span className="ml-auto text-[9px] font-black px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 animate-pulse">{item.badge}</span>
                                                    )}
                                                </Link>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </nav>

                    {/* Footer card + Sign Out */}
                    <div className="p-3 border-t space-y-2" style={{ borderColor: '#1e293b' }}>
                        <div className="rounded-lg p-3 flex items-center gap-3" style={{ background: '#111c2d' }}>
                            <Avatar className="h-9 w-9 rounded-full ring-2 ring-emerald-500/40">
                                <AvatarFallback className="rounded-full text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #2563eb 100%)' }}>
                                    {getInitials(user?.name)}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-white truncate">{user?.name}</p>
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider">{user?.role?.replace('_', ' ')}</p>
                            </div>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-bold text-rose-300 hover:text-white hover:bg-rose-600/90 border border-rose-500/30 hover:border-rose-500 transition-colors"
                            data-testid="sidebar-logout"
                        >
                            <LogOut className="h-4 w-4" />
                            Sign Out
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main */}
            <div className="lg:pl-72 pb-20 lg:pb-0">
                {/* Top bar — premium glassmorphic */}
                <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200">
                    <div className="flex items-center gap-3 px-4 sm:px-6 h-[72px]">
                        <button className="lg:hidden p-2 -ml-2 text-slate-700" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                            <Menu className="h-6 w-6" />
                        </button>

                        {/* Search (desktop) */}
                        <div className="hidden md:flex items-center gap-2 flex-1 max-w-md">
                            <div className="relative w-full">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search leads, RMs, payslips…"
                                    className="w-full pl-10 pr-4 py-2 text-sm bg-slate-100 border border-transparent focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15 rounded-lg outline-none transition-colors"
                                    data-testid="topbar-search"
                                />
                            </div>
                        </div>
                        <div className="flex-1 md:hidden" />

                        {/* Right cluster */}
                        <div className="flex items-center gap-2">
                            <NotificationBell />
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="flex items-center gap-2 rounded-lg px-2 hover:bg-slate-100" data-testid="user-menu-btn">
                                        <Avatar className="h-9 w-9 rounded-full ring-1 ring-slate-200">
                                            <AvatarFallback className="rounded-full text-white text-xs font-bold" style={{ background: 'linear-gradient(135deg, #16a34a 0%, #2563eb 100%)' }}>
                                                {getInitials(user?.name)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="hidden sm:block text-left">
                                            <p className="text-sm font-bold text-slate-900 leading-none">{user?.name}</p>
                                            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{user?.role?.replace('_', ' ')}</p>
                                        </div>
                                        <ChevronDown className="hidden sm:block h-4 w-4 text-slate-400" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-60 rounded-xl shadow-lg border-slate-200">
                                    <div className="px-3 py-2.5">
                                        <p className="text-sm font-bold text-slate-900">{user?.name}</p>
                                        <p className="text-xs text-slate-500">{user?.email}</p>
                                    </div>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="cursor-pointer rounded-md mx-1" data-testid="profile-link" onClick={() => navigate('/profile')}>
                                        <UserIcon className="mr-2 h-4 w-4" />Profile & Visiting Card
                                    </DropdownMenuItem>
                                    <DropdownMenuItem className="cursor-pointer rounded-md mx-1" onClick={() => navigate('/performance')}>
                                        <Sparkles className="mr-2 h-4 w-4" />My KPI
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem className="cursor-pointer rounded-md mx-1 text-red-600 focus:text-red-600" onClick={handleLogout} data-testid="logout-btn">
                                        <LogOut className="mr-2 h-4 w-4" />Sign out
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {/* Always-visible Logout button (no clicks/dropdown needed) */}
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-rose-600 hover:text-white hover:bg-rose-600 border border-rose-200 hover:border-rose-600 transition-colors"
                                title="Sign out"
                                data-testid="topbar-logout-btn"
                            >
                                <LogOut className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Logout</span>
                            </button>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto">
                    {children}
                </main>
            </div>

            {/* Mobile bottom navigation (RM-friendly) */}
            <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]" data-testid="mobile-bottom-nav">
                <div className="grid grid-cols-5 h-16">
                    {mobileNav.map((item) => {
                        const isActive = location.pathname === item.href;
                        return (
                            <Link
                                key={item.name}
                                to={item.href}
                                className={`flex flex-col items-center justify-center gap-0.5 transition-colors ${isActive ? 'text-blue-600' : 'text-slate-400 hover:text-slate-600'}`}
                                data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                            >
                                <item.icon className="h-5 w-5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">{item.name}</span>
                                {isActive && <div className="w-1 h-1 rounded-full bg-blue-600" />}
                            </Link>
                        );
                    })}
                </div>
            </nav>
        </div>
    );
};

export default Layout;
